// src/main.ts — Bootstrap (ARCHITECTURE §1/§12 WP9, DESIGN §13): ?anim=1 → animasyon doğrulama sahnesi; ?smoke=1 → duman testi;
// aksi hâlde menü → yükleme → Game.run → (Tekrar: aynı ayar, yeni seed | Menü). Stage / DebugDraw / Audio süreçte bir kez kurulur
// (F3 dinleyicisi tek), önbellekler (GLTF, proplar, klipler) menüye dönüşte kalır. ?stats=1 → HUD istatistik kutusu.
import { CHARACTERS } from './config/characters.ts';
import { Stage } from './view/stage.ts';
import { DebugDraw } from './view/debugDraw.ts';
import { Audio } from './view/audio.ts';
import { LoadProgress, assetUrl } from './view/assets.ts';
import { Game, charDefsOf, getClipData, loadMatchAssets, type GameExit } from './game/game.ts';
import { buildRoster, rosterRand, testPlayers } from './game/roster.ts';
import { padDiagnostics } from './input/gamepad.ts';
import { mountFullscreenButton } from './view/fullscreen.ts';
import { mountSettingsButton } from './view/settings.ts';
import { DEFAULT_CHOICE, hideLoading, runMenu, showLoading, type MenuChoice } from './game/menu.ts';
import { entryOf, type Entry } from './game/entry.ts';
import { loadPlayerName } from './game/playerName.ts';

const canvas = document.getElementById('canvas') as HTMLCanvasElement;
// Pad tanılama: konsolda `__pads()` → bağlı pad'lerin yuvası, kimliği, eşlemesi ve o an basılı tuşları.
// (Pad'in hangi yuvada olduğu ve eşlemesinin 'standard' olup olmadığı, "menüde çalışıyor maçta çalışmıyor" gibi
//  sorunların tek teşhis aracı; tarayıcı pad'i ilk düğmeye basılana kadar hiç göstermez.)
(globalThis as unknown as { __pads?: () => unknown[] }).__pads = padDiagnostics;
// Tam ekran düğmesi (sağ üst) + F kısayolu: menü, maç ve eğitim aynı düğmeyi paylaşsın diye süreçte BİR kez kurulur.
mountFullscreenButton();
const params = new URLSearchParams(location.search);

const newSeed = (): number => (Math.floor(Math.random() * 0x7fffffff) ^ (Date.now() & 0xffff)) | 0;

/** Menüden çıkan seçim → kadro (game/roster.ts: insanlar önce, insan + bot ≤ 8, bot karakterleri insanların dışından).
 *  `?players=N` menüdeki katılmayı ATLAR: N insan YUVASI kurulur (bot değil) — pad'i olmayan yuvanın girdisi nötr kalır,
 *  tarayıcı testinde sanal pad enjekte edilir (docs yok; yalnız hızlı deneme bayrağı). */
function rosterOf(choice: MenuChoice, rosterSeed: number): ReturnType<typeof buildRoster> {
  const forced = params.has('players') ? testPlayers(Number(params.get('players')) | 0, choice.charIdx) : null;
  const players = forced ?? (choice.players.length > 0 ? choice.players : DEFAULT_CHOICE.players);
  // Tohumlu çekiliş: bot karakterleri artık (seçim, rosterSeed) çiftinden yeniden üretilebilir. Ağ maçında bu
  // tohumu sunucu dağıtacak; yerelde her menü turunda bir kez çekilir (tekrar maçında kadro sabit kalsın diye
  // maç seed'inden AYRI). Bkz. NETWORK.md §2.4.
  // Ağ kadrosunu SUNUCU kuruyor; buraya yalnız yerel yollar (dövüş/eğitim) geliyor. `as` ile susturmak yerine
  // açık ifade: 'online' bu fonksiyona hiç ulaşmıyor.
  const mode = choice.mode === 'tutorial' ? 'tutorial' : 'fight';
  return buildRoster({ players, bots: choice.bots, difficulty: choice.difficulty, mode }, rosterRand(rosterSeed));
}

async function main(): Promise<void> {
  if (params.get('anim')) {
    const { startAnimScene } = await import('./anim/animScene.ts');
    await startAnimScene(canvas);
    return;
  }
  // ?portraits=1 → PORTRE STÜDYOSU (geliştirici aracı): karakter seçimindeki resimleri oyunun kendi
  // modellerinden üretir. Menü/oyun kurulmaz; çıktıyı tools/make-portraits.mjs diske yazar.
  if (params.get('portraits')) {
    const { runPortraitStudio } = await import('./game/portraits.ts');
    await runPortraitStudio();
    return;
  }
  if (params.get('smoke')) {
    const { runSmoke } = await import('./game/smoke.ts');
    await runSmoke(canvas, params);
    return;
  }
  const stage = new Stage(canvas, { quality: 'medium', auto: true });
  const dbg = new DebugDraw(stage.scene);
  dbg.bindKey(window);
  // Sesler proje içinde `public/audio/` altında → Vite kökten servis eder (/audio/<ad>.ogg; build'de dokunulmadan kopyalanır).
  // `available` null: SOUND_NAMES'in tamamının dosyası var (test/audio.test.ts bunu koruyor) → süzmeye gerek yok.
  const audio = new Audio(assetUrl('/audio'));
  // Ayarlar (sağ üst, tam ekranın solunda): ses seviyesi + M kısayolu. Kararı localStorage'da tutar; `?mute=1` ile zorlanabilir
  // (otomatik tarayıcı testleri bunu kullanır). Menü/maç/eğitim aynı düğmeyi paylaşsın diye süreçte BİR kez kurulur.
  mountSettingsButton(audio);
  const hudContainer = document.getElementById('hud-root') ?? undefined;
  // Ses kilidi: AudioContext yalnız KULLANICI JESTİNDE açılır. Menü kendi jestinde açıyor, ama ?tutorial=1 menüyü ATLIYOR →
  // doğrudan eğitime girildiğinde hiçbir ses duyulmuyordu (eğitim geri bildirimi sese dayanıyor, TUTORIAL §5). Tek seferlik kanca.
  const unlockAudio = (): void => {
    audio.unlock();
    removeEventListener('keydown', unlockAudio); removeEventListener('pointerdown', unlockAudio);
  };
  addEventListener('keydown', unlockAudio);
  addEventListener('pointerdown', unlockAudio);
  // players dizisi KOPYALANIR: menü/kadro onu her turda yeniden yazıyor, DEFAULT_CHOICE'un kaydı bozulmasın.
  let choice: MenuChoice = { ...DEFAULT_CHOICE, players: DEFAULT_CHOICE.players.map((p) => ({ ...p })) };
  if (params.has('char')) {
    choice.charIdx = Math.max(0, Math.min(CHARACTERS.length - 1, Number(params.get('char')) | 0));
    for (const p of choice.players) if (p.index === 0) p.charIdx = choice.charIdx;   // P1'in seçimi iki yerde de aynı olsun
  }
  choice.name = loadPlayerName(location.search);

  /**
   * ÇEVRİMİÇİ OTURUM: tek soket üzerinden lobi → maç → lobi → maç…
   *
   * Soketin sahibi `NetSession`; lobi ekranı onu yalnız boyuyor. Eskiden soketi lobi açıyordu ve her maç
   * turunda yenisi açılıyordu → sunucuda hayalet koltuk kalıp odayı kilitliyordu (NETWORK.md §13).
   * Döner: menüde gösterilecek gerekçe (varsa) — kullanıcı kendi vazgeçtiyse yok.
   */
  async function runOnlineSession(c: MenuChoice): Promise<string | null> {
    // Ağ kodu ilk pakete girmesin: yalnız buraya gelindiğinde yüklenir.
    const [{ NetSession, serverUrl }, { runNetLobby }] = await Promise.all([
      import('./net/session.ts'), import('./game/netLobby.ts'),
    ]);
    const clips = await getClipData();
    const session = new NetSession({
      url: serverUrl(params), name: c.name, clips, charIdx: c.charIdx, bots: c.bots, difficulty: c.difficulty,
    });
    let notice: string | null = null;
    try {
      for (;;) {
        const lobby = await runNetLobby(session);
        if (!lobby.ok) { if (!session.alive || lobby.reason !== 'vazgeçildi') notice = lobby.reason; break; }
        const netFighters = lobby.fighters.map((f) => ({ charIdx: f.charIdx, isBot: f.isBot, difficulty: 'normal' as const }));
        // Bağlantı ölümü daha VARLIKLAR YÜKLENİRKEN olabilir: gerekçe kaybolmasın diye kanca oyundan önce
        // kuruluyor (oyun henüz yokken yalnız `notice` yazılır, döngü aşağıda `session.alive` ile kırılır).
        let game: Game | null = null;
        session.onDead = (reason) => { notice = reason; game?.exit('leave'); };
        const progress = new LoadProgress();
        showLoading(progress);
        try {
          const assets = await loadMatchAssets(charDefsOf(netFighters), progress);
          const me = { index: 0, fighterId: lobby.yourFighterId, padSlot: -1, charIdx: netFighters[lobby.yourFighterId]?.charIdx ?? 0, ready: true };
          const g = new Game({
            stage, assets, fighters: netFighters, players: [me], seed: session.client.world?.seed ?? 0,
            audio, debugDraw: dbg, stats: params.has('stats'), hudContainer, net: session.client, countdown: true,
          });
          await g.init();
          game = g;
        } catch (e) {
          // Eksik/bozuk varlık TÜM uygulamayı düşürmesin: menüye gerekçesiyle dönülür (yerel yolda da
          // aynısı olmalı ama orası bugünkü davranışıyla bırakıldı — bu tur ağ yolunu açıyor).
          notice = `maç yüklenemedi: ${e instanceof Error ? e.message : String(e)}`;
          break;
        } finally {
          hideLoading();   // varlık 404'lerse yükleme ekranı asılı kalmasın
        }
        if (game === null || !session.alive) break;      // yükleme sırasında bağlantı öldü
        // Sunucu maçı kapattığında (herkes lobiye) oyuncunun panele tıklamasını BEKLEMEYİZ.
        session.onServerLobby = () => { game?.exit('menu'); };
        // Dünyayı ARTIK oyun döngüsü sürüyor; oturumun boşluk pompası sussun (çift ilerletme olmasın).
        session.driven = true;
        let exit: GameExit;
        try { exit = await game.run(); } finally { session.driven = false; game.dispose(); game = null; }
        session.onServerLobby = null; session.onDead = null;
        if (exit === 'leave' || !session.alive) break;
      }
    } finally {
      session.close('menüye dönüldü');
    }
    return notice;
  }

  // Derin bağlantılar (?net=1, ?tutorial=1) artık ayrı kod dalı değil: döngünün İLK turunda tüketilen bir tohum.
  let pending: Entry = entryOf(params);
  let notice: string | null = null;
  for (;;) {
    if (pending !== 'menu') { choice = { ...choice, mode: pending === 'online' ? 'online' : 'tutorial' }; pending = 'menu'; }
    else { choice = await runMenu(stage, choice, audio, { notice }); notice = null; }
    if (choice.mode === 'online') { notice = await runOnlineSession(choice); continue; }
    const tutorial = choice.mode === 'tutorial';
    // Kadro tohumu menü turunda BİR kez: tekrar maçı (rematch) yeni maç seed'i alır ama aynı kadroyla oynanır
    // (varlıklar da bir kez yüklenir). `?seed=` verilmişse kadro da o tohumdan → tam tekrarlanabilir koşum.
    const rosterSeed = params.has('seed') ? Number(params.get('seed')) | 0 : newSeed();
    const { fighters, players } = rosterOf(choice, rosterSeed);
    const progress = new LoadProgress();
    showLoading(progress);
    const assets = await loadMatchAssets(charDefsOf(fighters), progress);
    hideLoading();
    let exit: GameExit = 'rematch';
    while (exit === 'rematch') {
      const seed = params.has('seed') ? Number(params.get('seed')) | 0 : newSeed();
      const game = new Game({
        stage, assets, fighters, players, seed, audio, debugDraw: dbg, stats: params.has('stats'), hudContainer,
        // Eğitimde geri sayım yok (TUTORIAL §1 "süre baskısı yok"): panel görünür görünmez oyuncu serbest.
        tutorial, countdown: !tutorial,
        // ?record=1 → maçın tüm girdileri kaydedilir (NETWORK.md §7.3); window.__game.recording() ile alınır.
        // Eğitimde kapalı: TutorialMode dünyayı tick akışının dışında değiştiriyor, kayıt oynatılamaz.
        record: params.has('record') && !tutorial, rosterSeed,
      });
      await game.init();
      exit = await game.run();
      game.dispose();
    }
  }
}

void main().catch((err: unknown) => {
  console.error('ringout: başlatma hatası', err);
  const label = document.getElementById('loading-label');
  if (label) { label.textContent = 'HATA: ' + String(err); const el = document.getElementById('loading'); if (el) el.hidden = false; }
});

